import { useState, useEffect, useRef, type ImgHTMLAttributes } from "react";

type LazyImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "loading"> & {
  /** Wrapper element className (controls layout/aspect). */
  wrapperClassName?: string;
  /** Force eager loading (defaults to lazy). */
  eager?: boolean;
};

/**
 * Image with a blurred low-quality placeholder (LQIP) effect.
 *
 * Renders the image inside a wrapper that shows a soft blurred gradient
 * background until the real image decodes, then fades + un-blurs into place.
 * Improves perceived load time for lazy-loaded media without needing to
 * generate per-image base64 thumbnails at build time.
 */
export function LazyImage({
  wrapperClassName,
  className,
  eager,
  onLoad,
  alt,
  style,
  sizes,
  srcSet,
  ...rest
}: LazyImageProps) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  // `sizes` is only valid HTML when a `srcset` is present.
  const resolvedSizes = srcSet ? sizes : undefined;


  // If the image is already cached/decoded before hydration, onLoad never
  // fires — check on mount so it doesn't stay invisible.
  useEffect(() => {
    const el = imgRef.current;
    if (el?.complete && el.naturalWidth > 0) setLoaded(true);
  }, []);

  return (
    <span
      className={`lqip-wrapper relative block overflow-hidden bg-muted ${wrapperClassName ?? ""}`}
      data-loaded={loaded ? "true" : "false"}
    >
      {/* Soft blurred placeholder shimmer */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 bg-gradient-to-br from-muted via-muted-foreground/10 to-muted transition-opacity duration-500 ${loaded ? "opacity-0" : "opacity-100"}`}
        style={{ filter: "blur(20px)" }}
      />
      <img
        {...rest}
        ref={imgRef}
        alt={alt}
        srcSet={srcSet}
        sizes={resolvedSizes}

        loading={eager ? "eager" : "lazy"}
        decoding="async"
        onError={() => setLoaded(true)}
        onLoad={(e) => {
          setLoaded(true);
          onLoad?.(e);
        }}
        className={`${className ?? ""} transition-[opacity,filter,transform] duration-500 ease-out ${
          loaded ? "opacity-100 blur-0 scale-100" : "opacity-0 blur-md scale-[1.03]"
        }`}
        style={style}
      />
    </span>
  );
}
